import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { Icon } from './icon';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import WexlerIcon from '@/assets/img/wexler-icon.png';
import { Button } from '@/new/components/shadcn/button';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'pwa-prompt';
const NAV_COUNT_KEY = 'pwa-prompt-nav-count';
const NAV_THRESHOLD = 7;
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

type DismissState = {
  count: number;
  dismissedAt?: number;
};

function getDismissState(): DismissState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return { count: 0 };
}

function saveDismissState(state: DismissState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function getNavCount(): number {
  return parseInt(localStorage.getItem(NAV_COUNT_KEY) ?? '0', 10);
}

function incrementNavCount(): number {
  const next = getNavCount() + 1;
  localStorage.setItem(NAV_COUNT_KEY, String(next));
  return next;
}

function shouldShowPrompt(): boolean {
  if (getNavCount() < NAV_THRESHOLD) return false;

  const state = getDismissState();

  // Dismissed twice — never show again
  if (state.count >= 2) return false;

  // Dismissed once — only show again after 2 weeks
  if (state.count === 1 && state.dismissedAt) {
    return Date.now() - state.dismissedAt >= TWO_WEEKS_MS;
  }

  return true;
}

// Capture the event globally so it's not missed before React mounts
let savedPrompt: BeforeInstallPromptEvent | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  savedPrompt = e as BeforeInstallPromptEvent;
});

export function PwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(savedPrompt);
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      savedPrompt = e as BeforeInstallPromptEvent;
      setDeferredPrompt(savedPrompt);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Track navigations and re-evaluate visibility
  useEffect(() => {
    incrementNavCount();
    setVisible(shouldShowPrompt());
  }, [location.pathname]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    trackCustomEvent(TrackingEventsV2.PwaPromptInstallClicked, { outcome });
    if (outcome === 'accepted') {
      savedPrompt = null;
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    trackCustomEvent(TrackingEventsV2.PwaPromptDismissed);
    const state = getDismissState();
    saveDismissState({ count: state.count + 1, dismissedAt: Date.now() });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {deferredPrompt && visible && (
        <motion.div
          initial={{ opacity: 0, filter: 'blur(2px)', y: -8 }}
          animate={{
            opacity: 1,
            filter: 'blur(0px)',
            y: 0,
            transition: { duration: 0.2, ease: 'easeOut', delay: 0.5 },
          }}
          exit={{ opacity: 0, filter: 'blur(2px)', y: -8, transition: { duration: 0.2, ease: 'easeOut' } }}
          className="fixed top-4 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-4 rounded-[18px] border bg-black p-4.5 text-white shadow-lg/4"
        >
          <Button
            onClick={handleDismiss}
            className="absolute -top-1 -right-1 rounded-full border-gray-500 bg-gray-700 p-0.5 hover:border-gray-400 hover:bg-gray-600"
          >
            <Icon className="lucide--x text-xs text-gray-300!" />
          </Button>

          <div className="flex items-center gap-2.5 pr-4">
            <img src={WexlerIcon} alt="Wexler App Icon" className="size-12 drop-shadow-lg/6" />
            <div className="space-y-0.25">
              <p className="font-medium">Need facts at your fingertips?</p>
              <p className="text-xs text-gray-300">Access Wexler from your desktop</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 p-0.5">
            <Button className="group rounded-lg ring-offset-black hover:bg-white" onClick={handleInstall}>
              <Icon className="lucide--screen-share" />
              Add Wexler to desktop
              <Icon className="lucide--chevron-right -ml-2 opacity-0 duration-150 group-hover:ml-0 group-hover:opacity-100" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
